import { Fragment } from 'react';

/**
 * Minimalist markdown renderer for AI interpretations.
 *
 * Handles ONLY what the AI is asked to produce:
 *   • **bold** segments → <strong>
 *   • paragraph breaks (\n\n) → spacing
 *   • single \n inside a paragraph → line break
 *
 * We deliberately do NOT use a full markdown library — fewer dependencies,
 * smaller bundle, no risk of unexpected HTML being injected from prompt
 * output. Anything that isn't **bold** is rendered as plain text.
 */
export default function RichText({
  text,
  className = '',
}: {
  text: string;
  className?: string;
}) {
  if (!text) return null;

  // Pre-pass:
  //   • Convert leading "### ..." / "#### ..." lines into **bold** lines
  //     so we have a single rendering path.
  //   • Strip markdown bullet markers ("* ", "- ", "• ") from the start
  //     of lines — Gemini likes to bullet-list its key points, and the
  //     literal asterisks look broken in our simple inline renderer.
  //     The bold heading that usually follows the bullet still renders.
  const normalised = text
    .split('\n')
    .map((line) => {
      // Strip leading bullet first
      const bulletStripped = line.replace(/^\s*[*•\-]\s+/, '');
      const m = /^\s*#{1,6}\s+(.+?)\s*$/.exec(bulletStripped);
      return m ? `**${m[1].replace(/\*+/g, '').trim()}**` : bulletStripped;
    })
    .join('\n');

  // Split into paragraphs first, then handle inline bold.
  const paragraphs = normalised.split(/\n{2,}/);

  return (
    <div className={className}>
      {paragraphs.map((para, pi) => (
        <p key={pi} className={pi > 0 ? 'mt-3' : ''}>
          {renderInline(para)}
        </p>
      ))}
    </div>
  );
}

function renderInline(text: string) {
  // Split on **bold** segments. Even-index slices are plain, odd are bold.
  const parts = text.split(/\*\*(.+?)\*\*/g);
  // Preserve single newlines inside a paragraph by walking each part.
  return parts.map((part, i) => {
    const isBold = i % 2 === 1;
    const lines = part.split('\n');
    const content = lines.map((line, li) => (
      <Fragment key={li}>
        {line}
        {li < lines.length - 1 && <br />}
      </Fragment>
    ));
    return isBold
      ? <strong key={i} className="font-semibold text-gold-100">{content}</strong>
      : <Fragment key={i}>{content}</Fragment>;
  });
}
